/**
 * Brand "how it works" step illustrations — 72x72 SVGs drawn in the homepage
 * visual language (grass baseline, #16a34a / #0f1d14 / #fbbf24 palette,
 * #f0fdf4 / #fff card-body fills). Extracted from
 * components/home/how-it-works-illustrated.tsx so both the (client) homepage
 * section and (server-rendered) tool pages like /diagnose share one copy.
 *
 * These are pure, hook-free SVG components with NO "use client" directive, so
 * they render in both server and client trees. Keep them that way.
 */

const PRIMARY = "#16a34a"

// Phone snapping a blade of grass, with a shutter-frame overlay and a sun spark.
export function StepPhoneCamera() {
  return (
    <svg width="72" height="72" viewBox="0 0 72 72" fill="none" aria-hidden="true">
      <rect x="22" y="10" width="28" height="40" rx="4" stroke="#0f1d14" strokeWidth="2" fill="#fff" />
      <rect x="26" y="16" width="20" height="22" rx="1.5" fill="#f0fdf4" stroke={PRIMARY} strokeWidth="1.5" />
      <path d="M28 19v3M28 19h3M44 19v3M44 19h-3M28 35v-3M28 35h3M44 35v-3M44 35h-3" stroke={PRIMARY} strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="36" cy="44" r="2.5" stroke="#0f1d14" strokeWidth="1.5" />
      <path d="M14 62c2-6 4-9 6-10M22 62c1-7 3-11 5-12M30 62c0-8 2-12 4-13M40 62c1-8 3-11 6-12M48 62c2-7 5-10 8-11M56 62c2-5 4-8 6-9" stroke={PRIMARY} strokeWidth="2" strokeLinecap="round" />
      <path d="M8 62h56" stroke="#0f1d14" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M52 12l1.5 3 3 1.5-3 1.5-1.5 3-1.5-3-3-1.5 3-1.5z" fill="#fbbf24" />
    </svg>
  )
}

// Map pin dropped over a lawn — the "add your ZIP / your location" step.
export function StepLocationPin() {
  return (
    <svg width="72" height="72" viewBox="0 0 72 72" fill="none" aria-hidden="true">
      <path d="M36 12c-8 0-14 6-14 14 0 10 14 22 14 22s14-12 14-22c0-8-6-14-14-14z" fill="#fff" stroke="#0f1d14" strokeWidth="2" strokeLinejoin="round" />
      <path d="M36 12c-8 0-14 6-14 14 0 10 14 22 14 22s14-12 14-22c0-8-6-14-14-14z" fill={PRIMARY} fillOpacity="0.08" />
      <circle cx="36" cy="26" r="6.5" fill={PRIMARY} stroke="#0f1d14" strokeWidth="1.4" />
      <circle cx="36" cy="26" r="2.4" fill="#fff" />
      <path d="M14 62c2-6 4-9 6-10M22 62c1-7 3-11 5-12M30 62c0-8 2-12 4-13M40 62c1-8 3-11 6-12M48 62c2-7 5-10 8-11M56 62c2-5 4-8 6-9" stroke={PRIMARY} strokeWidth="2" strokeLinecap="round" />
      <path d="M8 62h56" stroke="#0f1d14" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M54 12l1.5 3 3 1.5-3 1.5-1.5 3-1.5-3-3-1.5 3-1.5z" fill="#fbbf24" />
    </svg>
  )
}

// Magnifier over a grass blade with a green check — inspection / diagnosis.
export function StepMagnifier() {
  return (
    <svg width="72" height="72" viewBox="0 0 72 72" fill="none" aria-hidden="true">
      <path d="M20 58c0-14 4-24 12-32" stroke={PRIMARY} strokeWidth="2.5" strokeLinecap="round" />
      <path d="M30 58c-1-12 2-22 8-30" stroke={PRIMARY} strokeWidth="2.5" strokeLinecap="round" />
      <path d="M40 58c2-10 6-18 12-22" stroke="#86efac" strokeWidth="2.5" strokeLinecap="round" />
      <path d="M8 58h56" stroke="#0f1d14" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="34" cy="28" r="14" fill="#fff" stroke="#0f1d14" strokeWidth="2" />
      <circle cx="34" cy="28" r="14" fill={PRIMARY} fillOpacity="0.08" />
      <path d="M28 36c0-5 1-9 4-12M34 36c0-6 1-10 3-13M40 36c-1-4 0-8 2-11" stroke={PRIMARY} strokeWidth="1.5" strokeLinecap="round" />
      <path d="M26 36h16" stroke="#0f1d14" strokeWidth="1" strokeLinecap="round" />
      <path d="M44 38l10 10" stroke="#0f1d14" strokeWidth="3" strokeLinecap="round" />
      <circle cx="54" cy="18" r="6" fill={PRIMARY} />
      <path d="M51 18l2 2 4-4" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

// Year-plan checklist card with a grass baseline — the seasonal plan step.
export function StepSeasonRing() {
  return (
    <svg width="72" height="72" viewBox="0 0 72 72" fill="none" aria-hidden="true">
      <rect x="12" y="12" width="48" height="44" rx="3" fill="#fff" stroke="#0f1d14" strokeWidth="1.6" />
      <rect x="12" y="12" width="48" height="9" rx="3" fill={PRIMARY} />
      <rect x="12" y="18" width="48" height="3" fill={PRIMARY} />
      <text x="36" y="18.6" textAnchor="middle" fontSize="4.4" fontWeight="800" fill="#fff" fontFamily="ui-sans-serif, system-ui" letterSpacing="0.05em">YEAR PLAN</text>

      <rect x="16" y="25" width="3.2" height="3.2" rx="0.7" fill={PRIMARY} />
      <path d="M16.6 26.6l0.9 0.9 1.4-1.4" stroke="#fff" strokeWidth="0.9" strokeLinecap="round" strokeLinejoin="round" />
      <rect x="21" y="25.6" width="22" height="2" rx="1" fill="#9ca3af" />
      <rect x="45" y="25.6" width="11" height="2" rx="1" fill="#86efac" />

      <rect x="16" y="31" width="3.2" height="3.2" rx="0.7" fill={PRIMARY} />
      <path d="M16.6 32.6l0.9 0.9 1.4-1.4" stroke="#fff" strokeWidth="0.9" strokeLinecap="round" strokeLinejoin="round" />
      <rect x="21" y="31.6" width="20" height="2" rx="1" fill="#9ca3af" />
      <rect x="43" y="31.6" width="13" height="2" rx="1" fill="#86efac" />

      <rect x="16" y="37" width="3.2" height="3.2" rx="0.7" fill="none" stroke={PRIMARY} strokeWidth="1" />
      <rect x="21" y="37.6" width="24" height="2" rx="1" fill="#9ca3af" />

      <rect x="16" y="43" width="3.2" height="3.2" rx="0.7" fill="none" stroke="#9ca3af" strokeWidth="1" />
      <rect x="21" y="43.6" width="22" height="2" rx="1" fill="#e5e7eb" />

      <rect x="16" y="49" width="3.2" height="3.2" rx="0.7" fill="none" stroke="#9ca3af" strokeWidth="1" />
      <rect x="21" y="49.6" width="20" height="2" rx="1" fill="#e5e7eb" />

      <path d="M53 53c0-4 1-6 3-9" stroke={PRIMARY} strokeWidth="2" strokeLinecap="round" />
      <path d="M53 53c-1.5-1-2.5-2-3-4" stroke="#86efac" strokeWidth="1.6" strokeLinecap="round" />

      <path d="M8 64h56" stroke="#0f1d14" strokeWidth="1.4" strokeLinecap="round" />
      <path d="M14 64c0-3 1-5 2-6M22 64c0-4 1-6 2-7M30 64c0-3 1-5 2-6M40 64c0-4 1-6 2-7M50 64c0-3 1-5 2-6M58 64c0-4 1-6 2-7" stroke={PRIMARY} strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  )
}
